import { useEffect } from 'react';
import { useChatbotStore } from '@/stores/chatbot/chatbotStore';
import { useUserStore } from '@/stores/user/userStore';
import { getLLMModels } from '@/services/model.service';

/**
 * Custom hook to fetch and cache LLM models
 * Fetches models only once per tier type and stores them in Zustand store
 * Subsequent calls return cached data without making API calls
 * 
 * @returns {Array} Array of LLM models
 */
export const useLLMModels = () => {
  const { user } = useUserStore();
  const llmModels = useChatbotStore((state) => state.llmModels);
  const llmModelsLastFetchedTier = useChatbotStore((state) => state.llmModelsLastFetchedTier);
  const setLLMModels = useChatbotStore((state) => state.setLLMModels);

  // Use owner's tier for team members to get the correct models
  const effectiveTierType = user?.owner_tier_type || user?.tier_type;

  useEffect(() => {
    // Only fetch if:
    // 1. Models are not cached yet (empty array)
    // 2. Effective tier type has changed (different from last fetch)
    // 3. Effective tier type is available
    if (
      effectiveTierType &&
      (llmModels.length === 0 || llmModelsLastFetchedTier !== effectiveTierType)
    ) {
      const fetchModels = async () => {
        try {
          const response = await getLLMModels({ tier_type: effectiveTierType });
          setLLMModels(response.data || [], effectiveTierType);
        } catch (error) {
          console.error('Failed to fetch LLM models:', error);
          // Set empty array to avoid infinite retries
          setLLMModels([], effectiveTierType);
        }
      };

      fetchModels();
    }
  }, [effectiveTierType, llmModels.length, llmModelsLastFetchedTier, setLLMModels]);

  return llmModels;
};

export default useLLMModels;

